import { type ProductVariant } from '@commercetools/platform-sdk'
import { Link, useLoaderData, useLocation } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ImageComponent from '#app/components/common/image-component'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader } from '#app/routes/_public+/product+/$styleNumber'
import { getAttributeValue } from '#app/utils/attribute-util'
import { cn } from '#app/utils/misc'
import { type StyleVariant, type PlentyVariant } from '#types/product'
import { findVariantBySku } from '..'
import { getGenderBasedOnCategorySlug } from '../utils'
import { PriceTag } from './pdp-price-tag'

interface ProductOverviewProps {
	selectedSku: string | null | undefined
	setSelectedSku: (val: string) => void
	setCurrentImages?: any
}

const ProductOverview = ({
	setCurrentImages,
	selectedSku,
	setSelectedSku,
}: ProductOverviewProps) => {
	const [moreStylesOpen, setMoreStylesOpen] = useState(false)
	const location = useLocation()
	const loaderData = useLoaderData<typeof loader>()
	const [hoveredColor, setHoveredColor] = useState<string | null>(null)
	const { trackAmplitudeEvent } = useAmplitudeContext()

	const productData = loaderData?.productData
	const activeStyle =
		productData?.styleAvailableVariants.find(
			(item: { active: any }) => item.active,
		) || productData!.styleAvailableVariants[0]
	const brand = activeStyle.variants[0].attributes?.find(
		(item: { name: string }) => item.name === 'brand',
	)?.value

	const color = activeStyle.variants[0].attributes?.find(
		(item: { name: string }) => item.name === 'color',
	)?.value

	let { t } = useTranslation('product_details_page')

	const firstFiveElements = productData?.styleAvailableVariants.slice(0, 5)
	const restOfElements = productData?.styleAvailableVariants.slice(5)

	useEffect(() => {
		if (
			!firstFiveElements?.some((variant: { active: any }) => variant.active)
		) {
			setMoreStylesOpen(true)
		}
	}, [firstFiveElements])
	const [initialImages, setInitialImages] = useState<
		Array<{ original: string; thumbnail: string }>
	>([])

	useEffect(() => {
		const newImages =
			activeStyle.variants[0].images?.map((image: { url: any }) => ({
				original: image.url,
				thumbnail: image.url,
			})) || []

		if (JSON.stringify(newImages) !== JSON.stringify(initialImages)) {
			setInitialImages(newImages)
			setCurrentImages(newImages)
		}
	}, [activeStyle.variants, setCurrentImages, initialImages])

	const productCategorySlug =
		productData?.productInfo.categories[0].obj?.key || ''

	const genderBrand = getGenderBasedOnCategorySlug(
		productCategorySlug as string,
	)
	const brandLink = location.search
		? `/products/${genderBrand}${location.search}&brand=${brand}`
		: `/products/${genderBrand}?brand=${brand}`

	return (
		<div className="mt-4 md:mt-0">
			<div className="flex flex-col gap-y-2">
				<div className="flex w-full flex-row items-end justify-between md:flex-col-reverse md:items-start md:gap-2">
					<div className="flex flex-col">
						<PlentyHeading as="h7">{productData.name}</PlentyHeading>

						<PlentyBody
							size="xs"
							className="flex items-end text-beige-100"
							primaryHeading
						>
							{`${getAttributeValue(
								productData.productInfo.masterVariant as ProductVariant,
								'code_name',
							)}`}
						</PlentyBody>
					</div>
					<Link to={brandLink} className="-mb-0.5 flex items-end">
						<PlentyBody
							size="xs"
							className="text-light-grey-90 transition-all hover:underline"
						>
							{brand}
						</PlentyBody>
					</Link>
				</div>
				<>
					{selectedSku && selectedSku !== '' ? (
						<PriceTag
							activeStyle={activeStyle as StyleVariant}
							variant={
								findVariantBySku(
									activeStyle.variants as PlentyVariant[],
									selectedSku,
								) as PlentyVariant
							}
							isSelectedSku
							storeParams={loaderData?.storeParams}
						/>
					) : (
						<PriceTag
							activeStyle={activeStyle as StyleVariant}
							variant={activeStyle.variants[0] as PlentyVariant}
							isSelectedSku={false}
							storeParams={loaderData?.storeParams}
						/>
					)}
				</>
				{color && (
					<PlentyBody size="md">
						{t('common:colour_header')}:
						<span> {hoveredColor ? hoveredColor : color.en} </span>
					</PlentyBody>
				)}
			</div>

			<div className="hidden flex-col pt-4 md:flex">
				<div>
					{/* Desktop Images View */}
					<div className="hidden flex-row flex-nowrap gap-2 overflow-y-auto scrollbar-hide md:flex lg:flex-wrap">
						{firstFiveElements &&
							firstFiveElements.map((styleVariant, index) => (
								<Link
									key={styleVariant.lowestPrice.id}
									reloadDocument={false}
									className={cn(
										'relative w-[75px] min-w-[75px]',
										!styleVariant.isAvailableInSelectedStore &&
											!styleVariant.isAvailableInOtherStores
											? 'opacity-50'
											: '',
									)}
									onMouseEnter={() => {
										const images = styleVariant.variants[0].images?.map(
											image => ({
												original: image.url,
												thumbnail: image.url,
											}),
										)
										setCurrentImages(images)

										// Find the color attribute in the attributes array and extract its English value.
										const colorAttr = styleVariant.variants[0].attributes?.find(
											attr => attr.name === 'color',
										)
										const colorValue = colorAttr ? colorAttr.value.en : null

										setHoveredColor(colorValue) // Update the hovered color with the extracted value
									}}
									onMouseLeave={() => {
										setCurrentImages(initialImages)
										setHoveredColor(null) // Reset hovered color
									}}
									to={{
										search: `?style=${
											styleVariant.variants[0].styleVariantKey
										}${
											loaderData?.storeParams.length
												? `&store=${loaderData?.storeParams.join(',')}`
												: ''
										}`,
									}}
									onClick={() => {
										trackAmplitudeEvent('pdp style option click', {
											oldStyle: `${productData.productInfo.key}?style=${activeStyle.variants[0].styleVariantKey}`,
											newStyle: `${productData.productInfo.key}?style= ${styleVariant.variants[0].styleVariantKey}`,
										})
										setSelectedSku('')
									}}
									state={location.state ? location.state : null}
								>
									<ImageComponent
										size="thumbnail"
										className={`h-[102px] w-[75px] ${
											styleVariant.active
												? 'border-2 border-black-90'
												: 'border-0'
										} rounded-lg`}
										src={
											styleVariant.variants.length &&
											styleVariant.variants[0].images
												? `${styleVariant.variants[0].images[0].url.replace(
														'stormpreview',
														'stormthumbnail',
												  )}`
												: ''
										}
										alt={`Style ${index}`}
									/>

									{styleVariant.isAvailableInOtherStores &&
										!styleVariant.isAvailableInSelectedStore &&
										loaderData?.storeParams?.length > 0 && (
											<div className="absolute left-0 right-0 top-1/2 mx-1 -translate-y-1/2 transform rounded-sm bg-white px-1 py-1 text-center opacity-80">
												<PlentyBody
													size="xxs"
													className="leading-tight text-black-90"
												>
													{t('other_store')}
												</PlentyBody>
											</div>
										)}
								</Link>
							))}
						{restOfElements && restOfElements.length > 0 && !moreStylesOpen && (
							<div
								key={'plus-one'}
								className="w-[75px] min-w-[75px]"
								onClick={() => {
									setMoreStylesOpen(!moreStylesOpen)
								}}
							>
								<div
									className={`flex h-[102px] w-[75px] items-center justify-center rounded-lg border border-beige-70 hover:cursor-pointer`}
								>
									+{restOfElements.length}
								</div>
							</div>
						)}
						{restOfElements &&
							moreStylesOpen &&
							restOfElements.map((styleVariant, index) => {
								return (
									<Link
										key={styleVariant.lowestPrice.id}
										reloadDocument={false}
										className={cn(
											'relative w-[75px] min-w-[75px]',
											!styleVariant.isAvailableInSelectedStore &&
												!styleVariant.isAvailableInOtherStores
												? 'opacity-50'
												: '',
										)}
										onClick={() => {
											trackAmplitudeEvent('pdp style option click', {
												oldStyle: `${productData.productInfo.key}?style=${activeStyle.variants[0].styleVariantKey}`,
												newStyle: `${productData.productInfo.key}?style= ${styleVariant.variants[0].styleVariantKey}`,
											})
											setSelectedSku('')
										}}
										onMouseEnter={() => {
											const images = styleVariant.variants[0].images?.map(
												image => ({
													original: image.url,
													thumbnail: image.url,
												}),
											)
											setCurrentImages(images)

											// Find the color attribute in the attributes array and extract its English value.
											const colorAttr =
												styleVariant.variants[0].attributes?.find(
													attr => attr.name === 'color',
												)
											const colorValue = colorAttr ? colorAttr.value.en : null

											setHoveredColor(colorValue) // Update the hovered color with the extracted value
										}}
										onMouseLeave={() => {
											setCurrentImages(initialImages)
											setHoveredColor(null) // Reset hovered color
										}}
										to={{
											search: `?style=${
												styleVariant.variants[0].styleVariantKey
											}${
												loaderData?.storeParams.length
													? `&store=${loaderData?.storeParams.join(',')}`
													: ''
											}`,
										}}
										state={location.state ? location.state : null}
									>
										<ImageComponent
											size="thumbnail"
											className={`h-[102px] w-[75px] ${
												styleVariant.active
													? 'border-2 border-black-90'
													: 'border-0'
											} rounded-lg`}
											src={
												(styleVariant.variants.length &&
													styleVariant.variants[0].images?.[0]?.url) ||
												''
											}
											alt={`Style ${index}`}
										/>

										{styleVariant.isAvailableInOtherStores &&
											!styleVariant.isAvailableInSelectedStore &&
											loaderData?.storeParams?.length > 0 && (
												<div className="absolute left-0 right-0 top-1/2 mx-1 -translate-y-1/2 transform rounded-sm bg-white px-1 py-1 text-center opacity-80">
													<PlentyBody
														size="xxs"
														className="leading-tight text-black-90"
													>
														{t('other_store')}
													</PlentyBody>
												</div>
											)}
									</Link>
								)
							})}
					</div>
				</div>
			</div>
		</div>
	)
}

export default ProductOverview
